import React, { useEffect, useMemo, useState } from 'react';
import type { User, PathwayBlock, OjtSession, OjtMode } from '../../../types';
import { DataService } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { Wrench, Plus, Clock, ChevronRight, AlertTriangle, Layers } from 'lucide-react';

interface OjtBlockViewProps {
  user: User;
  block: PathwayBlock;
  pathwayId: string;
  onStartSession: (sessionId: string | null) => void;
}

export const OjtBlockView: React.FC<OjtBlockViewProps> = ({ user, block, pathwayId, onStartSession }) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<OjtSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const all = await DataService.getOjtSessionsForCandidate(user.id);
        const forBlock = all.filter((s: OjtSession) => s.blockId === block.id && s.pathwayId === pathwayId);
        setSessions(forBlock);
      } catch (e) {
        console.error('Failed to load OJT sessions', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.id, block.id, pathwayId]);

  const totalLoggedMinutes = sessions.reduce((sum, s) => sum + (s.loggedTimeMinutes || 0), 0);
  const totalLoggedHours = (totalLoggedMinutes / 60).toFixed(1);
  const expectedHours = block.ojtConfig?.expectedDurationHours || 0;
  const modes: OjtMode[] = block.ojtConfig?.modes || [];

  /** Aggregated minutes spent in each configured mode across all sessions for this block. */
  const modeTotals = useMemo(() => {
    if (modes.length === 0) return [] as { mode: OjtMode; minutes: number; meetsRequired: boolean }[];
    const totals: Record<string, number> = {};
    for (const sess of sessions) {
      const log = sess.modeTimeLog || [];
      for (const seg of log) {
        const mins = seg.endTime !== undefined
          ? (seg.durationMinutes ?? Math.max(0, Math.round((seg.endTime - seg.startTime) / 60000)))
          : Math.max(0, Math.round((Date.now() - seg.startTime) / 60000));
        totals[seg.modeId] = (totals[seg.modeId] || 0) + mins;
      }
    }
    return modes.map(mode => {
      const minutes = totals[mode.id] || 0;
      const meetsRequired = !mode.requiredMinutes || minutes >= mode.requiredMinutes;
      return { mode, minutes, meetsRequired };
    });
  }, [sessions, modes]);

  const formatMinutes = (mins: number): string => {
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  const statusColors: Record<string, string> = {
    SCHEDULED: 'bg-aero-100 text-aero-600',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    REVIEW_PENDING: 'bg-yellow-100 text-yellow-700',
    COMPLETED: 'bg-green-100 text-green-700',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      {/* Block header */}
      <div className="bg-white rounded-xl border border-aero-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-aero-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Wrench className="w-5 h-5 text-aero-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold text-aero-400 uppercase tracking-wide mb-1">{t('candidate.block.ojt.title')}</p>
            <h2 className="text-lg font-bold text-aero-900">{block.name}</h2>
            {block.description && <p className="text-sm text-aero-600 mt-1">{block.description}</p>}
          </div>
        </div>

        {/* Hours progress */}
        {expectedHours > 0 && (
          <div className="mt-4 pt-4 border-t border-aero-100">
            <div className="flex justify-between text-xs text-aero-500 mb-1.5">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t('candidate.block.ojt.loggedHours', { hours: totalLoggedHours })}</span>
              <span>{t('candidate.block.ojt.expectedHours', { hours: expectedHours })}</span>
            </div>
            <div className="h-2 bg-aero-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, (parseFloat(totalLoggedHours) / expectedHours) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Per-mode time totals */}
        {modeTotals.length > 0 && (
          <div className="mt-4 pt-4 border-t border-aero-100 space-y-2">
            <p className="text-[11px] font-bold text-aero-500 uppercase tracking-wider flex items-center gap-1">
              <Layers className="w-3 h-3" /> {t('candidate.block.ojt.timePerMode')}
            </p>
            {modeTotals.map(({ mode, minutes, meetsRequired }) => (
              <div key={mode.id} className="flex items-center gap-2 text-xs">
                <span className="w-2 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: mode.color }} />
                <span className="flex-1 text-aero-700 truncate">{mode.label}</span>
                <span className={`font-medium ${mode.requiredMinutes && !meetsRequired ? 'text-red-500' : 'text-aero-700'}`}>
                  {formatMinutes(minutes)}
                  {mode.requiredMinutes ? <span className="text-aero-400 font-normal"> / {formatMinutes(mode.requiredMinutes)}</span> : null}
                </span>
                {mode.requiredMinutes && !meetsRequired && (
                  <AlertTriangle className="w-3 h-3 text-red-500" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Start session button */}
      <button
        onClick={() => onStartSession(null)}
        className="w-full flex items-center justify-center gap-2 py-3.5 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold text-sm transition-colors shadow-sm"
      >
        <Plus className="w-5 h-5" />
        {t('candidate.block.ojt.newSession')}
      </button>

      {/* Sessions list */}
      <div>
        <h3 className="text-sm font-bold text-aero-700 mb-2">{t('candidate.block.ojt.sessions')}</h3>
        {sessions.length === 0 ? (
          <div className="bg-white rounded-xl border border-aero-100 p-4 text-center">
            <p className="text-sm text-aero-400">{t('candidate.block.ojt.noSessions')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map((session) => {
              const canResume = session.status === 'IN_PROGRESS' || session.status === 'SCHEDULED';
              return (
              <button
                key={session.id}
                onClick={() => canResume ? onStartSession(session.id) : undefined}
                disabled={!canResume}
                className={`w-full bg-white rounded-xl border border-aero-200 p-4 flex items-center justify-between text-left ${canResume ? 'hover:border-aero-400 cursor-pointer' : 'opacity-75 cursor-default'}`}
              >
                <div>
                  <p className="text-sm font-semibold text-aero-900">{session.title || `Session ${new Date(session.date).toLocaleDateString()}`}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${statusColors[session.status] || statusColors.SCHEDULED}`}>
                      {t(`candidate.block.ojt.status.${session.status}`)}
                    </span>
                    {session.loggedTimeMinutes > 0 && (
                      <span className="text-xs text-aero-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {(session.loggedTimeMinutes / 60).toFixed(1)}h
                      </span>
                    )}
                  </div>
                </div>
                {canResume && <ChevronRight className="w-4 h-4 text-aero-300" />}
              </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
